import { buildTimeConfig } from 'config'
import { usePrePopulateSponsorshipVoucherFlow } from 'modules/auth/flows/usePrePopulateSponsorshipVoucherFlow'
import { SignupFormData } from 'modules/auth/hooks/useSignupForm'
import { useSignupStoreError } from 'modules/auth/store/useSignupStore'
import { EnumSignupErrors } from 'modules/auth/types/signup'
import { Link } from 'modules/common/components/Link/Link'
import { MessageWithCheckbox } from 'modules/common/components/MessageWithCheckbox/MessageWithCheckbox'
import { PasswordTextField } from 'modules/common/components/_ui/FormControl/PasswordTextField/PasswordTextField'
import { Message } from 'modules/common/components/_ui/Message/Message'
import { PasswordHelper } from 'modules/common/components/_ui/PasswordHelper/PasswordHelper'
import { useCountryConfig } from 'modules/common/hooks/useCountryConfig'
import { useCountryExternalLinks } from 'modules/common/hooks/useCountryExternalLinks'
import { useTranslation } from 'modules/common/hooks/useTranslation'
import { Trans } from 'next-i18next'
import { InputHTMLAttributes } from 'react'
import { useFormContext } from 'react-hook-form'
import { SignupFormSponsorship } from '../../SignupFormSponsorship/SignupFormSponsorship'
import { SignupFormPasswordFieldConsentCheckbox } from './SignupFormPasswordFieldConsentCheckbox/SignupFormPasswordFieldConsentCheckbox'
import { SignupFormPasswordFieldOptionalConsentCheckbox } from './SignupFormPasswordFieldOptionalConsentCheckbox/SignupFormPasswordFieldOptionalConsentCheckbox'

type Props = Pick<InputHTMLAttributes<HTMLInputElement>, 'autoFocus'>

export const SignupFormPasswordField = ({ autoFocus }: Props) => {
  const { t } = useTranslation(['auth-components', 'common'])

  const { hasOptionalConsents } = useCountryConfig()

  const { isPrePopulateSponsorshipVoucherLoading } =
    usePrePopulateSponsorshipVoucherFlow()

  const externalLinks = useCountryExternalLinks()

  const signupError = useSignupStoreError()

  const {
    register,
    formState: { errors, dirtyFields, isSubmitted },
    control,
    watch,
  } = useFormContext<SignupFormData>()

  const { password } = watch()

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <PasswordTextField
          label={t('signupForm.password.label')}
          {...register('password')}
          control={control}
          // Remove error message, we do not want it displayed below the field
          error={
            errors.password
              ? { ...errors.password, message: '' }
              : errors.password
          }
          autoComplete="off"
          autoFocus={autoFocus}
          isRequired
          data-amp-mask=""
        />
        <PasswordHelper
          fieldError={errors.password}
          isDirty={!!password && dirtyFields.password}
        />
      </div>
      {isSubmitted && signupError === EnumSignupErrors.TECHNICAL_ERROR && (
        <Message
          title={t('signupForm.errors.technicalError.title')}
          content={t('signupForm.errors.technicalError.content')}
          type="error"
        />
      )}
      {buildTimeConfig.signupConfig.shouldDisplayOperatedBy && (
        <Message
          content={t('signupForm.password.operatedBy')}
          className="items-center"
          type="info"
          withIcon
        />
      )}
      <SignupFormSponsorship
        isPrePopulateSponsorshipVoucherLoading={
          isPrePopulateSponsorshipVoucherLoading
        }
      />
      <SignupFormPasswordFieldConsentCheckbox />
      {hasOptionalConsents && (
        <SignupFormPasswordFieldOptionalConsentCheckbox />
      )}
      {buildTimeConfig.signupConfig
        .shouldDisplayCommercialSolicitationOptIn && (
        <MessageWithCheckbox
          id="userAcceptsCommercialSolicitation"
          name="userAcceptsCommercialSolicitation"
          label={
            <Trans
              parent="p"
              components={{
                linkComponent: (
                  <Link
                    key="privacyPolicy"
                    href={externalLinks.brandPrivacyPolicyLink}
                    className="font-bold text-primary"
                  />
                ),
              }}
            >
              {t('signupForm.password.userAcceptsCommercialSolicitation.label')}
            </Trans>
          }
          control={control}
        />
      )}
    </div>
  )
}
